import { useEffect, useMemo, useState } from "react";
import { Plus, Check, Trash2, Pencil } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";
import PageHeader from "../components/PageHeader";
import FilterPopover from "../components/FilterPopover";
import { NewMoveInline, EditMoveInline } from "../components/MovesTimeline";
import { useAuth } from "../context/AuthContext";
import { useConfirm } from "../components/ConfirmModal";

const filters = [
  { k: "mine", label: "My Moves" },
  { k: "all", label: "All Moves" },
  { k: "overdue", label: "Overdue" },
  { k: "upcoming", label: "Upcoming" },
];

const statusColor = (s) => s === "overdue" ? "var(--pg-red)" : s === "done" ? "var(--pg-accent-deep)" : "var(--pg-text-dim)";

export default function Moves() {
  const { user: currentUser } = useAuth();
  const confirm = useConfirm();
  const [moves, setMoves] = useState([]);
  const [filter, setFilter] = useState("mine");
  const [users, setUsers] = useState([]);
  const [types, setTypes] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterState, setFilterState] = useState({ type: [], assignee: [], status: [] });

  const load = async () => {
    setLoading(true);
    try {
      const [m, u, t] = await Promise.all([
        api.get(`/moves?filter=${filter}`),
        api.get("/users"),
        api.get("/move-types"),
      ]);
      setMoves(m.data); setUsers(u.data); setTypes(t.data);
    } catch { toast.error("Failed to load"); }
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const userName = (id) => users.find(u => u.id === id)?.name || "—";

  const filteredMoves = useMemo(() => {
    let rows = moves;
    if (filterState.type.length > 0) {
      const set = new Set(filterState.type);
      rows = rows.filter((m) => set.has(m.move_type || ""));
    }
    if (filterState.assignee.length > 0) {
      const set = new Set(filterState.assignee);
      rows = rows.filter((m) => set.has(m.assigned_to));
    }
    if (filterState.status.length > 0) {
      const set = new Set(filterState.status);
      rows = rows.filter((m) => set.has(m.status || "pending"));
    }
    return rows;
  }, [moves, filterState]);

  const toggleDone = async (m) => {
    try {
      await api.patch(`/moves/${m.id}`, { status: m.status === "done" ? "pending" : "done" });
      load();
    } catch { toast.error("Failed"); }
  };

  const remove = async (id) => {
    if (!await confirm({ title: "Delete this move?", message: "This cannot be undone.", destructive: true, confirmLabel: "Delete" })) return;
    await api.delete(`/moves/${id}`);
    load();
  };

  return (
    <div data-testid="moves-page">
      <PageHeader title="Moves" subtitle={`${moves.length} moves · tasks & activities`}>
        <button className="pg-btn pg-btn-primary" onClick={() => setShowForm(true)} data-testid="new-move-btn">
          <Plus size={14} /> New Move
        </button>
      </PageHeader>

      <div className="px-8 py-4 flex items-center gap-2 flex-wrap" style={{ borderBottom: "1px solid var(--pg-border)" }}>
        {filters.map(f => (
          <button
            key={f.k}
            onClick={() => setFilter(f.k)}
            className={`pg-btn ${filter === f.k ? "pg-btn-active" : ""}`}
            data-testid={`filter-${f.k}`}
          >
            {f.label}
          </button>
        ))}
        <div style={{ marginLeft: "auto" }}>
          <FilterPopover
            testid="moves-filter"
            matchCount={filteredMoves.length}
            value={filterState}
            onChange={setFilterState}
            groups={[
              { key: "type",     title: "Type",     options: types.map((t) => ({ value: t.name, label: t.name })) },
              { key: "assignee", title: "Assignee", options: users.filter((u) => u.active !== false).map((u) => ({ value: u.id, label: u.name })) },
              {
                key: "status", title: "Status",
                options: [
                  { value: "pending", label: "Pending" },
                  { value: "done",    label: "Done" },
                ],
              },
            ]}
          />
        </div>
      </div>

      <div className="overflow-x-auto">
        {loading ? (
          <div className="px-8 py-20 text-center text-mute mono text-xs">loading…</div>
        ) : filteredMoves.length === 0 ? (
          <div className="px-8 py-12">
            <div className="empty-state">
              <div className="text-lg mb-2" style={{ color: "var(--pg-text)" }}>No moves</div>
              <div className="mono text-xs">Create a move to track your next action.</div>
            </div>
          </div>
        ) : (
          <table className="pg-table">
            <thead>
              <tr>
                <th></th>
                <th>Title</th><th>Type</th><th>Linked To</th>
                <th>Due Date</th><th>Assigned</th><th>Status</th><th></th>
              </tr>
            </thead>
            <tbody>
              {filteredMoves.map(m => {
                const isOverdue = m.effective_status === "overdue";
                const linked = m.linked_type
                  ? `${m.linked_type}${m.linked_name ? " - " + m.linked_name : ""}`
                  : "—";
                return (
                  <tr
                    key={m.id}
                    data-testid={`move-row-${m.id}`}
                    style={{
                      background: isOverdue ? "rgba(194,107,107,0.04)" : "",
                      borderLeft: `3px solid ${isOverdue ? "var(--pg-red)" : "rgb(168, 85, 247)"}`,
                    }}
                  >
                    <td onClick={(e) => { e.stopPropagation(); toggleDone(m); }} style={{ cursor: "pointer", width: 30 }}>
                      <Check size={14} style={{ color: m.status === "done" ? "var(--pg-accent-deep)" : "var(--pg-text-mute)" }} />
                    </td>
                    <td style={{ textDecoration: m.status === "done" ? "line-through" : "none", color: m.status === "done" ? "var(--pg-text-mute)" : undefined }}>
                      {m.title}
                    </td>
                    <td className="mono text-xs text-dim">{m.type}</td>
                    <td className="mono text-xs text-dim">{linked}</td>
                    <td className="mono text-xs text-dim">{(m.due_date || "").slice(0, 16).replace("T", " ")}</td>
                    <td className="text-dim text-sm">{userName(m.assigned_to)}</td>
                    <td><span className="mono text-xs uppercase" style={{ color: statusColor(m.effective_status) }}>{m.effective_status}</span></td>
                    <td style={{ width: 70 }}>
                      <div className="flex items-center gap-2 justify-end">
                        <button
                          onClick={(e) => { e.stopPropagation(); setEditing(m); }}
                          className="text-mute hover:text-[var(--pg-accent)]"
                          title="Edit"
                          data-testid={`moves-edit-${m.id}`}
                        >
                          <Pencil size={14} />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); remove(m.id); }}
                          className="text-mute hover:text-[var(--pg-red)]"
                          title="Delete"
                          data-testid={`moves-delete-${m.id}`}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {showForm && (
        <NewMoveInline
          types={types}
          users={users}
          defaultAssignee={currentUser?.id}
          onClose={() => setShowForm(false)}
          onCreated={() => { setShowForm(false); load(); }}
        />
      )}

      {editing && (
        <EditMoveInline
          move={editing}
          types={types}
          users={users}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}
